import {
  categorizeHttp,
  categorizeWebSocket,
  DEV_ONLY_CATEGORIES,
  NON_NETWORK_CATEGORIES,
  sanitizeUrl,
} from './categorize';
import { buildReport, LEVEL, reportToCsv } from './report';

/**
 * Development-only network meter: measures what the app CURRENTLY sends and
 * receives, without changing any of it. Installed once from main.jsx behind
 * FEATURES.NETWORK_METER (never in production — see ./index.js).
 *
 * How each transport is observed, all passively:
 *   WebSocket       window.WebSocket is replaced by a subclass. send() is
 *                   forwarded unchanged and then sized; received messages are
 *                   sized from a separate 'message' listener, so the page's own
 *                   onmessage handler is untouched. Nothing is parsed or
 *                   re-serialised.
 *   WebRTC          window.RTCPeerConnection is replaced by a subclass that only
 *                   remembers the instance; getStats() is read once per second
 *                   while a measurement runs.
 *   HTTP            a PerformanceObserver for 'resource' entries (level B), plus
 *                   thin fetch / XHR / sendBeacon wrappers that size request
 *                   bodies the page already holds (level A). Response bodies are
 *                   never read or cloned.
 *   Media elements  Chromium's decoder byte counters, read once per second.
 *
 * Outside a running measurement every hook is a single boolean check. The
 * meter itself makes no network request; exports are local Blob downloads.
 * Nothing stores a message body, a token, or an unsanitised URL.
 */

const encoder = new TextEncoder();
const TICK_MS = 1000;

const STAGE_BEFORE_FIRST = 'NO_STAGE_YET';

const now = () => performance.now();

// ---------------------------------------------------------------------------
// State

let context = { pageOrigin: '', apiBaseUrl: '', poseSocketUrl: '', isLocalDevServer: false };
let installed = false;
let running = false;
let startedAt = null; // performance.now() of the interval start
let stoppedAt = null;
let startedAtIso = null;
let stoppedAtIso = null;
let fromNavigationStart = false;
let scenario = null;
let notes = [];
let tickTimer = null;
let currentStage = STAGE_BEFORE_FIRST;
let stageTimeline = [];
let cacheStateAtStart = null;
let cacheStateAtStop = null;
const listeners = new Set();

let ws = {};
let wsConnections = [];
let http = {};
let httpUrls = {};
let requests = {};
let buckets = {};
let grandTotals = {};
let media = { available: false, decodedBytes: 0, elementsSeen: 0 };
const mediaEntries = new Map();
// False until the first scan of an interval: elements found by that scan were
// already there (baseline = their current count), later ones are new (baseline 0).
let mediaPrimed = false;

// Every socket/pc ever created, so connections opened before start() are known.
let wsSeq = 0;
const wsMeta = new WeakMap();
const wsCategoriesSeen = new Set();
// Open sockets (dropped on close), so start() can list the ones it inherits.
const liveSockets = new Set();
let pcSeq = 0;
const pcEntries = [];

function resetCounters() {
  ws = {};
  wsConnections = [];
  http = {};
  httpUrls = {};
  requests = {};
  buckets = {};
  grandTotals = {
    [LEVEL.A]: { upload: 0, download: 0 },
    [LEVEL.B]: { upload: 0, download: 0 },
  };
  media = { available: false, decodedBytes: 0, elementsSeen: 0 };
  mediaEntries.clear();
  mediaPrimed = false;
  stageTimeline = [];
  notes = [];
  cacheStateAtStart = null;
  cacheStateAtStop = null;
  pcEntries.forEach(entry => {
    entry.baseline = null;
    entry.last = null;
    entry.ignored = false;
    entry.carried = null;
    entry.counterResets = 0;
    entry.polls = 0;
    entry.pollErrors = 0;
  });
}
resetCounters();

const elapsedMs = () => {
  if (startedAt === null) {
    return 0;
  }
  return Math.max(0, (running ? now() : stoppedAt) - startedAt);
};

const secondIndex = at => Math.floor(Math.max(0, at - startedAt) / 1000);

function addBucket(key, at, upload, download) {
  const second = secondIndex(at);
  const map = (buckets[key] ??= {});
  const pair = (map[second] ??= [0, 0]);
  pair[0] += upload;
  pair[1] += download;
}

function addGrand(level, category, upload, download) {
  if (DEV_ONLY_CATEGORIES.has(category) || NON_NETWORK_CATEGORIES.has(category)) {
    return;
  }
  grandTotals[level].upload += upload;
  grandTotals[level].download += download;
}

const notify = () => {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (_error) {
      // A panel error must not stop the meter.
    }
  });
};

// ---------------------------------------------------------------------------
// Sizes

function payloadSize(data) {
  if (typeof data === 'string') {
    return { bytes: encoder.encode(data).byteLength, kind: 'text' };
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return { bytes: data.size, kind: 'binary' };
  }
  if (data instanceof ArrayBuffer) {
    return { bytes: data.byteLength, kind: 'binary' };
  }
  if (ArrayBuffer.isView(data)) {
    return { bytes: data.byteLength, kind: 'binary' };
  }
  return { bytes: null, kind: 'unknown' };
}

/** { bytes, exact } — bytes null when the body cannot be sized without reading it. */
function requestBodySize(body) {
  if (body === null || body === undefined) {
    return { bytes: 0, exact: true };
  }
  if (typeof body === 'string') {
    return { bytes: encoder.encode(body).byteLength, exact: true };
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return { bytes: body.size, exact: true };
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return { bytes: body.byteLength, exact: true };
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return { bytes: encoder.encode(body.toString()).byteLength, exact: true };
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    let total = 0;
    body.forEach(value => {
      total += typeof value === 'string' ? encoder.encode(value).byteLength : value.size;
    });
    return { bytes: total, exact: false };
  }
  return { bytes: null, exact: false };
}

// ---------------------------------------------------------------------------
// WebSocket

const emptySide = () => ({
  count: 0,
  bytes: 0,
  min: null,
  max: null,
  text: { count: 0, bytes: 0 },
  binary: { count: 0, bytes: 0 },
});

function wsAgg(category) {
  return (ws[category] ??= {
    connectionsOpened: 0,
    reconnections: 0,
    closeCodes: {},
    extensions: [],
    sent: emptySide(),
    received: emptySide(),
    discardedSends: 0,
    maxBufferedAmount: 0,
    unknownTypeMessages: 0,
    stages: {},
  });
}

function addToSide(side, size) {
  side.count += 1;
  side.bytes += size.bytes;
  side.min = side.min === null ? size.bytes : Math.min(side.min, size.bytes);
  side.max = side.max === null ? size.bytes : Math.max(side.max, size.bytes);
  const typed = side[size.kind];
  typed.count += 1;
  typed.bytes += size.bytes;
}

function addToStage(agg, direction, bytes) {
  const stage = (agg.stages[currentStage] ??= {
    sent: { count: 0, bytes: 0 },
    received: { count: 0, bytes: 0 },
  });
  stage[direction].count += 1;
  stage[direction].bytes += bytes;
}

function recordWsMessage(socket, direction, data) {
  const meta = wsMeta.get(socket);
  if (!meta) {
    return;
  }
  const agg = wsAgg(meta.category);
  const size = payloadSize(data);
  if (size.bytes === null) {
    agg.unknownTypeMessages += 1;
    return;
  }
  const at = now();
  if (direction === 'sent') {
    addToSide(agg.sent, size);
    agg.maxBufferedAmount = Math.max(agg.maxBufferedAmount, socket.bufferedAmount || 0);
    addBucket(`${LEVEL.A}|${meta.category}`, at, size.bytes, 0);
    addGrand(LEVEL.A, meta.category, size.bytes, 0);
  } else {
    addToSide(agg.received, size);
    addBucket(`${LEVEL.A}|${meta.category}`, at, 0, size.bytes);
    addGrand(LEVEL.A, meta.category, 0, size.bytes);
  }
  addToStage(agg, direction, size.bytes);
}

function connectionRecord(meta) {
  let record = wsConnections.find(entry => entry.id === meta.id);
  if (!record) {
    record = {
      id: meta.id,
      category: meta.category,
      url: meta.url,
      openedAtMs: null,
      closedAtMs: null,
      openedBeforeInterval: false,
      closeCode: null,
      wasClean: null,
      extensions: null,
    };
    wsConnections.push(record);
  }
  return record;
}

function installWebSocketTap() {
  const NativeWebSocket = window.WebSocket;
  if (!NativeWebSocket || NativeWebSocket.__networkMeter) {
    return;
  }

  class MeteredWebSocket extends NativeWebSocket {
    constructor(url, protocols) {
      if (protocols === undefined) {
        super(url);
      } else {
        super(url, protocols);
      }
      try {
        onSocketCreated(this, url, protocols);
      } catch (_error) {
        // Never break socket creation.
      }
    }

    send(data) {
      // Forwarded first and unchanged: a send that throws (CONNECTING) was never
      // transmitted, so it must not be counted.
      const result = super.send(data);
      if (running) {
        try {
          if (this.readyState === NativeWebSocket.OPEN) {
            recordWsMessage(this, 'sent', data);
          } else {
            // CLOSING/CLOSED: the browser silently discards it.
            const meta = wsMeta.get(this);
            if (meta) {
              wsAgg(meta.category).discardedSends += 1;
            }
          }
        } catch (_error) {
          /* noop */
        }
      }
      return result;
    }
  }
  MeteredWebSocket.__networkMeter = true;

  window.WebSocket = MeteredWebSocket;
}

function onSocketCreated(socket, url, protocols) {
  wsSeq += 1;
  const category = categorizeWebSocket(String(url), protocols, context);
  const meta = {
    id: `ws#${wsSeq}`,
    category,
    url: sanitizeUrl(String(url), context.pageOrigin),
    // A second socket of the same category over the page's lifetime is a
    // reconnection (the pose socket is only ever reopened, never run twice).
    isReconnection: wsCategoriesSeen.has(category),
    openedAt: null,
  };
  wsCategoriesSeen.add(category);
  wsMeta.set(socket, meta);

  socket.addEventListener('open', () => {
    meta.openedAt = now();
    liveSockets.add(socket);
    if (!running) {
      return;
    }
    const agg = wsAgg(category);
    agg.connectionsOpened += 1;
    if (meta.isReconnection) {
      agg.reconnections += 1;
    }
    if (socket.extensions && !agg.extensions.includes(socket.extensions)) {
      agg.extensions.push(socket.extensions);
    }
    const record = connectionRecord(meta);
    record.openedAtMs = Math.round(meta.openedAt - startedAt);
    record.extensions = socket.extensions || null;
  });

  socket.addEventListener('message', event => {
    if (running) {
      try {
        recordWsMessage(socket, 'received', event.data);
      } catch (_error) {
        /* noop */
      }
    }
  });

  socket.addEventListener('close', event => {
    liveSockets.delete(socket);
    if (!running) {
      return;
    }
    const agg = wsAgg(category);
    agg.closeCodes[event.code] = (agg.closeCodes[event.code] ?? 0) + 1;
    const record = connectionRecord(meta);
    record.closedAtMs = Math.round(now() - startedAt);
    record.closeCode = event.code;
    record.wasClean = event.wasClean;
  });
}

// ---------------------------------------------------------------------------
// WebRTC

function installRtcTap() {
  const NativePeerConnection = window.RTCPeerConnection;
  if (!NativePeerConnection || NativePeerConnection.__networkMeter) {
    return;
  }

  class MeteredPeerConnection extends NativePeerConnection {
    constructor(...args) {
      super(...args);
      try {
        pcSeq += 1;
        pcEntries.push({
          id: `pc#${pcSeq}`,
          pc: this,
          createdAt: now(),
          closedAt: null,
          ignored: false,
          baseline: null,
          last: null,
          carried: null,
          counterResets: 0,
          polls: 0,
          pollErrors: 0,
        });
      } catch (_error) {
        /* noop */
      }
    }
  }
  MeteredPeerConnection.__networkMeter = true;

  window.RTCPeerConnection = MeteredPeerConnection;
}

const zeroRtc = () => ({
  transportSent: 0,
  transportReceived: 0,
  pairSent: 0,
  pairReceived: 0,
  hasTransport: false,
  hasPair: false,
  inPayload: 0,
  inHeader: 0,
  inPackets: 0,
  outPayload: 0,
  outHeader: 0,
  outPackets: 0,
  dcSent: 0,
  dcReceived: 0,
  dcMessagesSent: 0,
  dcMessagesReceived: 0,
});

/** One pass over a stats report; each counter comes from exactly one report type. */
export function extractRtcCounters(stats) {
  const counters = zeroRtc();
  const selectedPairIds = new Set();
  stats.forEach(report => {
    if (report.type === 'transport') {
      counters.hasTransport = true;
      counters.transportSent += report.bytesSent ?? 0;
      counters.transportReceived += report.bytesReceived ?? 0;
      if (report.selectedCandidatePairId) {
        selectedPairIds.add(report.selectedCandidatePairId);
      }
    } else if (report.type === 'inbound-rtp' && report.kind === 'audio') {
      counters.inPayload += report.bytesReceived ?? 0;
      counters.inHeader += report.headerBytesReceived ?? 0;
      counters.inPackets += report.packetsReceived ?? 0;
    } else if (report.type === 'outbound-rtp' && report.kind === 'audio') {
      counters.outPayload += report.bytesSent ?? 0;
      counters.outHeader += report.headerBytesSent ?? 0;
      counters.outPackets += report.packetsSent ?? 0;
    } else if (report.type === 'data-channel') {
      counters.dcSent += report.bytesSent ?? 0;
      counters.dcReceived += report.bytesReceived ?? 0;
      counters.dcMessagesSent += report.messagesSent ?? 0;
      counters.dcMessagesReceived += report.messagesReceived ?? 0;
    }
  });
  // Candidate-pair bytes are a fallback only, for browsers with no transport
  // report; never added on top of it.
  stats.forEach(report => {
    if (
      report.type === 'candidate-pair' &&
      (selectedPairIds.size ? selectedPairIds.has(report.id) : report.nominated === true)
    ) {
      counters.hasPair = true;
      counters.pairSent += report.bytesSent ?? 0;
      counters.pairReceived += report.bytesReceived ?? 0;
    }
  });
  return counters;
}

const RTC_KEYS = Object.keys(zeroRtc()).filter(key => !key.startsWith('has'));

function rtcDelta(entry) {
  if (!entry.last) {
    return null;
  }
  const delta = {};
  RTC_KEYS.forEach(key => {
    delta[key] = entry.last[key] + (entry.carried?.[key] ?? 0) - (entry.baseline?.[key] ?? 0);
  });
  delta.hasTransport = entry.last.hasTransport;
  delta.hasPair = entry.last.hasPair;
  return delta;
}

const transportBytes = delta =>
  delta.hasTransport
    ? { sent: delta.transportSent, received: delta.transportReceived }
    : delta.hasPair
      ? { sent: delta.pairSent, received: delta.pairReceived }
      : null;

async function pollPeerConnections(at) {
  await Promise.all(
    pcEntries.map(async entry => {
      if (entry.ignored || (entry.closedAt !== null && entry.last)) {
        return;
      }
      if (!entry.baseline && entry.createdAt < startedAt && entry.pc.connectionState === 'closed') {
        // Closed before this interval began: nothing of it belongs here.
        entry.ignored = true;
        return;
      }
      const before = rtcDelta(entry);
      try {
        const counters = extractRtcCounters(await entry.pc.getStats());
        entry.polls += 1;
        if (!entry.baseline) {
          // Created inside the interval: counters started at zero, so setup
          // traffic (DTLS handshake, first packets) is included.
          entry.baseline = entry.createdAt >= startedAt ? zeroRtc() : counters;
        }
        if (entry.last) {
          // A counter going backwards is a reset (should not happen within one
          // RTCPeerConnection): keep what was already counted.
          const reset = RTC_KEYS.some(key => counters[key] < entry.last[key]);
          if (reset) {
            entry.counterResets += 1;
            entry.carried = entry.carried ?? zeroRtc();
            RTC_KEYS.forEach(key => {
              entry.carried[key] += entry.last[key];
            });
          }
        }
        entry.last = counters;
      } catch (_error) {
        entry.pollErrors += 1;
      }
      if (entry.pc.connectionState === 'closed' && entry.closedAt === null) {
        entry.closedAt = now();
      }
      const after = rtcDelta(entry);
      const beforeBytes = before ? transportBytes(before) : { sent: 0, received: 0 };
      const afterBytes = after ? transportBytes(after) : null;
      if (afterBytes && beforeBytes) {
        const up = afterBytes.sent - beforeBytes.sent;
        const down = afterBytes.received - beforeBytes.received;
        if (up || down) {
          addBucket(`${LEVEL.B}|voice-agent-webrtc`, at, up, down);
          addGrand(LEVEL.B, 'voice-agent-webrtc', up, down);
        }
      }
    })
  );
}

function rtcSnapshot() {
  const inInterval = pcEntries.filter(
    entry => entry.last && (entry.closedAt === null || entry.closedAt >= startedAt)
  );
  const totals = {
    transport: { sent: 0, received: 0 },
    transportSource: null,
    inboundAudio: { payloadBytes: 0, headerBytes: 0, packets: 0 },
    outboundAudio: { payloadBytes: 0, headerBytes: 0, packets: 0 },
    dataChannel: { bytesSent: 0, bytesReceived: 0, messagesSent: 0, messagesReceived: 0 },
  };
  const connections = inInterval.map(entry => {
    const delta = rtcDelta(entry);
    const bytes = transportBytes(delta);
    if (bytes) {
      totals.transport.sent += bytes.sent;
      totals.transport.received += bytes.received;
      totals.transportSource = delta.hasTransport ? 'transport' : 'candidate-pair';
    }
    totals.inboundAudio.payloadBytes += delta.inPayload;
    totals.inboundAudio.headerBytes += delta.inHeader;
    totals.inboundAudio.packets += delta.inPackets;
    totals.outboundAudio.payloadBytes += delta.outPayload;
    totals.outboundAudio.headerBytes += delta.outHeader;
    totals.outboundAudio.packets += delta.outPackets;
    totals.dataChannel.bytesSent += delta.dcSent;
    totals.dataChannel.bytesReceived += delta.dcReceived;
    totals.dataChannel.messagesSent += delta.dcMessagesSent;
    totals.dataChannel.messagesReceived += delta.dcMessagesReceived;
    return {
      id: entry.id,
      createdAtMs: Math.round(entry.createdAt - startedAt),
      closedAtMs: entry.closedAt === null ? null : Math.round(entry.closedAt - startedAt),
      createdBeforeInterval: entry.createdAt < startedAt,
      polls: entry.polls,
      pollErrors: entry.pollErrors,
      counterResets: entry.counterResets,
      transportSentBytes: bytes?.sent ?? null,
      transportReceivedBytes: bytes?.received ?? null,
    };
  });
  return { connections, totals };
}

// ---------------------------------------------------------------------------
// HTTP: Resource Timing (level B)

function inferCache(entry, crossOrigin) {
  if (entry.workerStart > 0) {
    return 'service-worker';
  }
  if (entry.deliveryType === 'cache') {
    return 'http-cache';
  }
  if (entry.transferSize > 0) {
    return 'network';
  }
  if (entry.transferSize === 0 && entry.encodedBodySize > 0) {
    return 'http-cache-inferred';
  }
  return crossOrigin ? 'size-restricted-or-unknown' : 'unknown-zero-sizes';
}

function httpAgg(category) {
  return (http[category] ??= {
    entries: 0,
    transferSize: 0,
    encodedBodySize: 0,
    decodedBodySize: 0,
    transferSizeUnavailable: 0,
    cache: {},
    viaServiceWorker: 0,
    failed: 0,
    httpErrors: 0,
    redirects: 0,
    repeatedNetworkDownloads: 0,
    initiatorTypes: {},
  });
}

function recordResourceEntry(entry) {
  if (startedAt === null) {
    return;
  }
  // Attributed to the interval by when the response finished.
  const endedAt = entry.responseEnd || entry.startTime + entry.duration;
  if (endedAt < startedAt || (!running && endedAt > stoppedAt)) {
    return;
  }

  const initiator = entry.entryType === 'navigation' ? 'navigation' : entry.initiatorType;
  const category = categorizeHttp(entry.name, initiator, context);
  const agg = httpAgg(category);
  let crossOrigin = false;
  try {
    crossOrigin = new URL(entry.name).origin !== context.pageOrigin;
  } catch (_error) {
    /* noop */
  }
  const cache = inferCache(entry, crossOrigin);
  // A service-worker answer reports transferSize 0 to the page whatever the SW
  // itself fetched, so its size is unknown here — not zero.
  const sizesUnavailable =
    cache === 'size-restricted-or-unknown' ||
    cache === 'unknown-zero-sizes' ||
    cache === 'service-worker';

  agg.entries += 1;
  agg.initiatorTypes[initiator] = (agg.initiatorTypes[initiator] ?? 0) + 1;
  agg.cache[cache] = (agg.cache[cache] ?? 0) + 1;
  if (cache === 'service-worker') {
    agg.viaServiceWorker += 1;
    agg.serviceWorkerDecodedBodySize =
      (agg.serviceWorkerDecodedBodySize ?? 0) + entry.decodedBodySize;
  }
  if (sizesUnavailable) {
    agg.transferSizeUnavailable += 1;
  } else {
    agg.transferSize += entry.transferSize;
    agg.encodedBodySize += entry.encodedBodySize;
    agg.decodedBodySize += entry.decodedBodySize;
    addBucket(`${LEVEL.B}|${category}`, endedAt, 0, entry.transferSize);
    addGrand(LEVEL.B, category, 0, entry.transferSize);
  }
  if (typeof entry.responseStatus === 'number') {
    if (entry.responseStatus === 0 && sizesUnavailable && !crossOrigin) {
      agg.failed += 1;
    } else if (entry.responseStatus >= 400) {
      agg.httpErrors += 1;
    }
  }
  if (entry.redirectStart > 0 || entry.redirectEnd > 0) {
    agg.redirects += 1;
  }

  const url = sanitizeUrl(entry.name, context.pageOrigin);
  const perUrl = (httpUrls[url] ??= {
    category,
    entries: 0,
    networkFetches: 0,
    transferSize: 0,
    encodedBodySize: 0,
    decodedBodySize: 0,
  });
  perUrl.entries += 1;
  if (!sizesUnavailable) {
    perUrl.transferSize += entry.transferSize;
    perUrl.encodedBodySize += entry.encodedBodySize;
    perUrl.decodedBodySize += entry.decodedBodySize;
  }
  if (cache === 'network') {
    perUrl.networkFetches += 1;
    if (perUrl.networkFetches > 1) {
      agg.repeatedNetworkDownloads += 1;
    }
  }
}

function installResourceObserver() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    // Keep the timeline buffer from filling on a long session, so buffered
    // entries are still there for an autostarted measurement.
    performance.setResourceTimingBufferSize?.(5000);
  } catch (_error) {
    /* noop */
  }
  try {
    const observer = new PerformanceObserver(list => {
      if (!running) {
        return;
      }
      list.getEntries().forEach(entry => {
        try {
          recordResourceEntry(entry);
        } catch (_error) {
          /* noop */
        }
      });
    });
    observer.observe({ type: 'resource', buffered: false });
  } catch (_error) {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// HTTP: request bodies (level A)

function requestAgg(category) {
  return (requests[category] ??= {
    requests: 0,
    bodyBytes: 0,
    bodyBytesLowerBound: 0,
    bodiesUnmeasurable: 0,
    failed: 0,
    aborted: 0,
    httpErrors: 0,
    byTransport: { fetch: 0, xhr: 0, beacon: 0 },
  });
}

function recordRequest(transport, rawUrl, body) {
  const category = categorizeHttp(String(rawUrl), null, context);
  const agg = requestAgg(category);
  agg.requests += 1;
  agg.byTransport[transport] += 1;
  const size = requestBodySize(body);
  if (size.bytes === null) {
    agg.bodiesUnmeasurable += 1;
  } else if (!size.exact) {
    agg.bodyBytesLowerBound += size.bytes;
  } else {
    agg.bodyBytes += size.bytes;
    if (size.bytes) {
      addBucket(`${LEVEL.A}|${category}|http-body`, now(), size.bytes, 0);
      addGrand(LEVEL.A, category, size.bytes, 0);
    }
  }
  return agg;
}

function installRequestTaps() {
  const nativeFetch = window.fetch;
  if (nativeFetch && !nativeFetch.__networkMeter) {
    const meteredFetch = function (input, init) {
      const args = arguments;
      const promise = nativeFetch.apply(this ?? window, args);
      if (!running) {
        return promise;
      }
      try {
        const isRequest = typeof Request !== 'undefined' && input instanceof Request;
        const url = isRequest ? input.url : String(input);
        // A Request object's body cannot be sized without cloning and reading it.
        const body =
          init && 'body' in init
            ? init.body
            : isRequest && input.body
              ? { unreadable: true }
              : null;
        const agg = recordRequest('fetch', url, body);
        promise.then(
          response => {
            if (response && response.status >= 400) {
              agg.httpErrors += 1;
            }
          },
          error => {
            if (error?.name === 'AbortError') {
              agg.aborted += 1;
            } else {
              agg.failed += 1;
            }
          }
        );
      } catch (_error) {
        /* noop */
      }
      return promise;
    };
    meteredFetch.__networkMeter = true;
    window.fetch = meteredFetch;
  }

  const proto = window.XMLHttpRequest?.prototype;
  if (proto && !proto.open.__networkMeter) {
    const nativeOpen = proto.open;
    const nativeSend = proto.send;
    const xhrUrls = new WeakMap();
    proto.open = function (method, url) {
      try {
        xhrUrls.set(this, String(url));
      } catch (_error) {
        /* noop */
      }

      return nativeOpen.apply(this, arguments);
    };
    proto.open.__networkMeter = true;
    proto.send = function (body) {
      if (running) {
        try {
          const agg = recordRequest('xhr', xhrUrls.get(this) ?? '', body);
          let aborted = false;
          this.addEventListener(
            'abort',
            () => {
              aborted = true;
              agg.aborted += 1;
            },
            { once: true }
          );
          this.addEventListener(
            'loadend',
            () => {
              if (aborted) {
                return;
              }
              if (this.status >= 400) {
                agg.httpErrors += 1;
              } else if (this.status === 0) {
                agg.failed += 1;
              }
            },
            { once: true }
          );
        } catch (_error) {
          /* noop */
        }
      }

      return nativeSend.apply(this, arguments);
    };
  }

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const nativeBeacon = navigator.sendBeacon;
    if (!nativeBeacon.__networkMeter) {
      const meteredBeacon = function (url, data) {
        const accepted = nativeBeacon.apply(navigator, arguments);
        if (running && accepted) {
          try {
            recordRequest('beacon', url, data);
          } catch (_error) {
            /* noop */
          }
        }
        return accepted;
      };
      meteredBeacon.__networkMeter = true;
      navigator.sendBeacon = meteredBeacon;
    }
  }
}

// ---------------------------------------------------------------------------
// Media elements (Chromium decoder counters, level B proxy)

function pollMediaElements() {
  if (typeof document === 'undefined') {
    return;
  }
  document.querySelectorAll('video, audio').forEach(element => {
    // srcObject = camera preview or the WebRTC voice sink: not a download, and
    // the voice bytes are already in getStats().
    if (element.srcObject || !element.currentSrc) {
      return;
    }
    const video = element.webkitVideoDecodedByteCount;
    const audio = element.webkitAudioDecodedByteCount;
    if (typeof video !== 'number' && typeof audio !== 'number') {
      return;
    }
    media.available = true;
    const value = (video || 0) + (audio || 0);
    let entry = mediaEntries.get(element);
    if (!entry) {
      entry = {
        baseline: fromNavigationStart || mediaPrimed ? 0 : value,
        last: value,
        carried: 0,
        src: element.currentSrc,
      };
      mediaEntries.set(element, entry);
      media.elementsSeen += 1;
    }
    if (value < entry.last || element.currentSrc !== entry.src) {
      // New source loaded into the same element: the counter restarts at 0.
      entry.carried += entry.last - entry.baseline;
      entry.baseline = 0;
      entry.src = element.currentSrc;
    }
    entry.last = value;
  });
  mediaPrimed = true;
  let total = 0;
  mediaEntries.forEach(entry => {
    total += entry.carried + entry.last - entry.baseline;
  });
  media.decodedBytes = total;
}

// ---------------------------------------------------------------------------
// Environment + service-worker cache state

async function readCacheState() {
  const state = {
    serviceWorkerControlled: null,
    cacheNames: null,
    poseModelCached: null,
    poseWasmCached: null,
  };
  try {
    state.serviceWorkerControlled = Boolean(navigator.serviceWorker?.controller);
  } catch (_error) {
    /* noop */
  }
  try {
    if (typeof caches !== 'undefined') {
      state.cacheNames = await caches.keys();
      state.poseModelCached = Boolean(await caches.match('/models/pose_landmarker_heavy.task'));
      state.poseWasmCached = Boolean(await caches.match('/wasm/vision_wasm_internal.wasm'));
    }
  } catch (_error) {
    /* noop */
  }
  return state;
}

function readEnvironment() {
  const connection = navigator.connection || null;
  return {
    userAgent: navigator.userAgent,
    platform: navigator.userAgentData?.platform ?? navigator.platform ?? null,
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    deviceMemoryGb: navigator.deviceMemory ?? null,
    connection: connection
      ? {
          effectiveType: connection.effectiveType ?? null,
          downlinkMbps: connection.downlink ?? null,
          rttMs: connection.rtt ?? null,
          saveData: connection.saveData ?? null,
        }
      : null,
    isLocalDevServer: context.isLocalDevServer,
    pageOrigin: context.pageOrigin,
    route: typeof location !== 'undefined' ? location.pathname : null,
  };
}

// ---------------------------------------------------------------------------
// Public API

async function tick() {
  if (!running) {
    return;
  }
  const at = now();
  try {
    await pollPeerConnections(at);
  } catch (_error) {
    /* noop */
  }
  try {
    pollMediaElements();
  } catch (_error) {
    /* noop */
  }
  notify();
}

export function installMeter(options) {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;
  context = { ...context, ...options };
  installWebSocketTap();
  installRtcTap();
  installRequestTaps();
  installResourceObserver();
}

export function isRunning() {
  return running;
}

/**
 * `fromNavigationStart`: count from page load (time origin), including the
 * document and every resource already in the timeline — the first-use and
 * repeat-use scenarios. Otherwise the interval starts now (active-session).
 */
export function start({ scenario: label = null, fromNavigationStart: fromNav = false } = {}) {
  if (running) {
    return;
  }
  resetCounters();
  scenario = label;
  fromNavigationStart = fromNav;
  startedAt = fromNav ? 0 : now();
  startedAtIso = new Date(performance.timeOrigin + startedAt).toISOString();
  stoppedAt = null;
  stoppedAtIso = null;
  running = true;
  stageTimeline.push({ stage: currentStage, atMs: 0 });

  // Sockets already open when the interval starts: listed, not counted as opened.
  liveSockets.forEach(socket => {
    const meta = wsMeta.get(socket);
    if (meta) {
      const record = connectionRecord(meta);
      record.openedBeforeInterval = true;
      record.extensions = socket.extensions || null;
      const agg = wsAgg(meta.category);
      if (socket.extensions && !agg.extensions.includes(socket.extensions)) {
        agg.extensions.push(socket.extensions);
      }
    }
  });
  void readCacheState().then(state => {
    cacheStateAtStart = state;
  });

  if (fromNav) {
    try {
      performance
        .getEntriesByType('navigation')
        .concat(performance.getEntriesByType('resource'))
        .forEach(entry => recordResourceEntry(entry));
    } catch (_error) {
      /* noop */
    }
  }

  void tick();
  tickTimer = window.setInterval(() => void tick(), TICK_MS);
  notify();
}

export async function stop() {
  if (!running) {
    return;
  }
  // Final samples before the interval closes.
  try {
    await pollPeerConnections(now());
    pollMediaElements();
  } catch (_error) {
    /* noop */
  }
  running = false;
  stoppedAt = now();
  stoppedAtIso = new Date(performance.timeOrigin + stoppedAt).toISOString();
  window.clearInterval(tickTimer);
  tickTimer = null;
  cacheStateAtStop = await readCacheState();
  notify();
}

export function reset() {
  if (running) {
    window.clearInterval(tickTimer);
    tickTimer = null;
  }
  running = false;
  startedAt = null;
  stoppedAt = null;
  startedAtIso = null;
  stoppedAtIso = null;
  scenario = null;
  fromNavigationStart = false;
  resetCounters();
  notify();
}

export function note(text) {
  if (typeof text === 'string' && text.trim()) {
    // Free text from the developer running the test; capped, never auto-filled.
    notes.push({ atMs: Math.round(elapsedMs()), text: text.slice(0, 500) });
    notify();
  }
}

// STAGE_STATUS numbers -> names, from constants/constants.js.
let stageNames = {};
export function setStageNames(map) {
  stageNames = Object.fromEntries(Object.entries(map).map(([name, value]) => [value, name]));
}

export function setStage(stageValue) {
  const name =
    stageValue === undefined || stageValue === null
      ? STAGE_BEFORE_FIRST
      : (stageNames[stageValue] ?? `STAGE_${stageValue}`);
  if (name === currentStage) {
    return;
  }
  currentStage = name;
  if (running) {
    stageTimeline.push({ stage: name, atMs: Math.round(elapsedMs()) });
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshotRaw() {
  return {
    meta: {
      scenario,
      running,
      fromNavigationStart,
      startedAtIso,
      stoppedAtIso,
      durationMs: Math.round(elapsedMs()),
      currentStage,
      environment: readEnvironment(),
      cacheStateAtStart,
      cacheStateAtStop,
      notes,
      wsSocketsCreatedSincePageLoad: wsSeq,
      peerConnectionsCreatedSincePageLoad: pcSeq,
    },
    ws,
    wsConnections,
    http,
    httpUrls,
    requests,
    rtc: rtcSnapshot(),
    media,
    buckets,
    grandTotals,
    stageTimeline,
  };
}

export function report() {
  return buildReport(snapshotRaw());
}

export function csv() {
  return reportToCsv(report());
}

function download(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const fileStem = () =>
  `network-meter-${scenario ?? 'session'}-${(startedAtIso ?? new Date().toISOString()).replace(/[:.]/g, '-')}`;

export function downloadJson() {
  download(`${fileStem()}.json`, JSON.stringify(report(), null, 2), 'application/json');
}

export function downloadCsv() {
  download(`${fileStem()}.csv`, csv(), 'text/csv');
}
